import React, { useState, useEffect } from 'react';
import {
  Text,
  Button,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
  ProgressBar,
} from '@fluentui/react-components';
import {
  ArrowLeft20Regular,
  Checkmark20Regular,
  Warning20Regular,
  ErrorCircle20Regular,
} from '@fluentui/react-icons';
import { policyService } from '../../services/policyService';
import { DomainAssignment, ApprovedDomain, AssignmentResult } from './types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('24px'),
  },
  header: {
    marginBottom: '8px',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
    marginTop: '4px',
  },
  summaryCard: {
    ...shorthands.padding('16px'),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
  },
  summaryTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    marginBottom: '12px',
  },
  assignmentList: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px'),
  },
  assignmentItem: {
    display: 'flex',
    alignItems: 'flex-start',
    ...shorthands.gap('12px'),
    ...shorthands.padding('12px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
  },
  domainName: {
    fontFamily: 'monospace',
    fontSize: '13px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    minWidth: '200px',
  },
  arrow: {
    color: tokens.colorNeutralForeground3,
    marginTop: '2px',
  },
  policyList: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('4px'),
  },
  policyName: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
  },
  skippedText: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  warningBox: {
    display: 'flex',
    alignItems: 'flex-start',
    ...shorthands.gap('8px'),
    ...shorthands.padding('12px'),
    backgroundColor: tokens.colorPaletteYellowBackground2,
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    ...shorthands.border('1px', 'solid', tokens.colorPaletteYellowBorder1),
  },
  warningText: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
  },
  progressSection: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px'),
  },
  progressText: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
  resultsList: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
    marginTop: '12px',
  },
  resultItem: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    ...shorthands.padding('8px', '12px'),
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
  },
  successItem: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
  },
  errorItem: {
    backgroundColor: tokens.colorPaletteRedBackground2,
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '16px',
    ...shorthands.padding('16px', '0', '0', '0'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
});

interface ReviewAndConfirmProps {
  assignments: DomainAssignment[];
  domains: ApprovedDomain[];
  onBack: () => void;
  onComplete: (results: AssignmentResult[]) => void;
}

export const ReviewAndConfirm: React.FC<ReviewAndConfirmProps> = ({
  assignments,
  domains,
  onBack,
  onComplete,
}) => {
  const styles = useStyles();
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentOperation, setCurrentOperation] = useState('');
  const [results, setResults] = useState<AssignmentResult[]>([]);
  const [policyNames, setPolicyNames] = useState<Map<string, string>>(new Map());

  // Load policy names
  useEffect(() => {
    loadPolicyNames();
  }, [assignments]);

  const loadPolicyNames = async () => {
    try {
      const allPolicies = await policyService.getPolicies();
      const nameMap = new Map<string, string>();
      allPolicies.forEach(p => nameMap.set(p.id, p.name));
      setPolicyNames(nameMap);
    } catch (err) {
      console.error('Failed to load policy names:', err);
    }
  };

  const assignmentsToApply = assignments.filter(a => a.status === 'pending' && a.policyIds.length > 0);
  const skippedAssignments = assignments.filter(a => a.status === 'skipped' || a.policyIds.length === 0);

  const handleApply = async () => {
    setApplying(true);
    setProgress(0);
    const newResults: AssignmentResult[] = [];

    // Group assignments by policy to minimize API calls
    const assignmentsByPolicy = new Map<string, string[]>();
    
    assignmentsToApply.forEach(assignment => {
      assignment.policyIds.forEach(policyId => {
        if (!assignmentsByPolicy.has(policyId)) {
          assignmentsByPolicy.set(policyId, []);
        }
        assignmentsByPolicy.get(policyId)!.push(assignment.domainName);
      });
    });

    const totalOperations = assignmentsByPolicy.size;
    let completedOperations = 0;

    // Apply assignments policy by policy
    const policyEntries = Array.from(assignmentsByPolicy.entries());
    for (const [policyId, domainNames] of policyEntries) {
      const policyName = policyNames.get(policyId) || policyId;
      setCurrentOperation(`Adding domains to ${policyName}...`);

      try {
        // Get current policy to retrieve existing domains
        const policies = await policyService.getPolicies();
        const policy = policies.find(p => p.id === policyId);
        
        if (!policy) {
          throw new Error(`Policy ${policyName} not found`);
        }

        // Use allowedDomains (like PolicyDetails.tsx does) and include inherited_domains
        // inherited_domains must always be included in the request
        const currentAllowedDomains = (policy as any).allowedDomains || [];
        const inheritedDomains = (policy as any).inherited_domains || [];
        const currentBlockedDomains = (policy as any).blockedDomains || [];

        // Merge: inherited domains + current allowed + new domains (avoid duplicates with Set)
        const allAllowedDomains = Array.from(new Set([
          ...inheritedDomains,
          ...currentAllowedDomains,
          ...domainNames
        ]));

        console.log('🔍 Domain assignment debug:', {
          policyId,
          policyName,
          inheritedCount: inheritedDomains.length,
          inheritedDomains,
          currentAllowedCount: currentAllowedDomains.length,
          currentAllowedDomains,
          newDomainsToAdd: domainNames,
          finalAllowedCount: allAllowedDomains.length,
          finalAllowedDomains: allAllowedDomains,
        });

        // Call the backend API directly using apiClient
        // This matches the PUT /api/policies/:id/domains endpoint
        const { apiClient } = await import('../../services/apiClient');
        await apiClient.put(`/policies/${policyId}/domains`, {
          allowedDomains: allAllowedDomains,
          blockedDomains: currentBlockedDomains,
        });

        // Record success for each domain
        domainNames.forEach((domainName: string) => {
          newResults.push({
            success: true,
            domainName,
            policyNames: [policyName],
          });
        });
      } catch (err: any) {
        console.error(`Failed to add domains to ${policyName}:`, err);
        
        // Record failure for each domain
        domainNames.forEach((domainName: string) => {
          newResults.push({
            success: false,
            domainName,
            policyNames: [policyName],
            error: err.message || 'Unknown error',
          });
        });
      }

      completedOperations++;
      setProgress((completedOperations / totalOperations) * 100);
    }

    setResults(newResults);
    setApplying(false);
    setCurrentOperation('Complete!');
    
    // Wait a moment to show results, then complete
    setTimeout(() => {
      onComplete(newResults);
    }, 2000);
  };

  const getDomainById = (domainId: string) => {
    return domains.find(d => d.id === domainId);
  };

  if (applying || results.length > 0) {
    return (
      <div className={styles.container} data-testid="review-and-confirm-progress">
        <div className={styles.header} data-testid="review-and-confirm-progress-header">
          <Text className={styles.title} data-testid="review-and-confirm-progress-title">
            {results.length > 0 ? 'Assignment Complete' : 'Applying Assignments...'}
          </Text>
        </div>

        <div className={styles.progressSection} data-testid="review-and-confirm-progress-section">
          {applying && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }} data-testid="review-and-confirm-current-operation-row">
                <Spinner size="medium" data-testid="review-and-confirm-progress-spinner" />
                <Text className={styles.progressText} style={{ fontWeight: '600' }} data-testid="review-and-confirm-current-operation-text">
                  {currentOperation}
                </Text>
              </div>
              <ProgressBar value={progress} data-testid="review-and-confirm-progress-bar" />
            </>
          )}

          {results.length > 0 && (
            <div className={styles.resultsList} data-testid="review-and-confirm-results-list">
              {results.map((result, idx) => (
                <div
                  key={idx}
                  className={`${styles.resultItem} ${
                    result.success ? styles.successItem : styles.errorItem
                  }`}
                  data-testid={`review-and-confirm-result-item-${idx}`}
                >
                  {result.success ? (
                    <Checkmark20Regular style={{ color: tokens.colorPaletteGreenForeground2 }} data-testid={`review-and-confirm-result-success-icon-${idx}`} />
                  ) : (
                    <ErrorCircle20Regular style={{ color: tokens.colorPaletteRedForeground2 }} data-testid={`review-and-confirm-result-error-icon-${idx}`} />
                  )}
                  <Text style={{ fontSize: '13px' }} data-testid={`review-and-confirm-result-text-${idx}`}>
                    {result.domainName} → {result.policyNames.join(', ')}
                    {result.error && ` (${result.error})`}
                  </Text>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container} data-testid="review-and-confirm">
      <div className={styles.header} data-testid="review-and-confirm-header">
        <Text className={styles.subtitle} data-testid="review-and-confirm-subtitle">
          Please review the assignments before applying
        </Text>
      </div>

      {assignmentsToApply.length > 0 && (
        <div className={styles.summaryCard} data-testid="review-and-confirm-assignment-summary-card">
          <Text className={styles.summaryTitle} data-testid="review-and-confirm-assignment-summary-title">
            Domains to Assign ({assignmentsToApply.length})
          </Text>
          <div className={styles.assignmentList} data-testid="review-and-confirm-assignment-list">
            {assignmentsToApply.map((assignment, domainIndex) => {
              const domain = getDomainById(assignment.domainId);
              return (
                <div key={assignment.domainId} className={styles.assignmentItem} data-testid={`review-and-confirm-assignment-item-${domainIndex}`}>
                  <Text className={styles.domainName} data-testid={`review-and-confirm-assignment-domain-${domainIndex}`}>{assignment.domainName}</Text>
                  <Text className={styles.arrow} data-testid={`review-and-confirm-assignment-arrow-${domainIndex}`}>→</Text>
                  <div className={styles.policyList} data-testid={`review-and-confirm-assignment-policy-list-${domainIndex}`}>
                    {assignment.policyIds.map((policyId, policyIndex) => (
                      <Text key={policyId} className={styles.policyName} data-testid={`review-and-confirm-assignment-policy-${domainIndex}-${policyIndex}`}>
                        • {policyNames.get(policyId) || policyId}
                      </Text>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {skippedAssignments.length > 0 && (
        <div className={styles.summaryCard} data-testid="review-and-confirm-skipped-summary-card">
          <Text className={styles.summaryTitle} data-testid="review-and-confirm-skipped-summary-title">
            Skipped Domains ({skippedAssignments.length})
          </Text>
          <div className={styles.assignmentList} data-testid="review-and-confirm-skipped-list">
            {skippedAssignments.map((assignment, assignmentIndex) => (
              <div key={assignment.domainId} className={styles.assignmentItem} data-testid={`review-and-confirm-skipped-item-${assignmentIndex}`}>
                <Text className={styles.domainName} data-testid={`review-and-confirm-skipped-domain-${assignmentIndex}`}>{assignment.domainName}</Text>
                <Text className={styles.skippedText} data-testid={`review-and-confirm-skipped-text-${assignmentIndex}`}>No policies selected</Text>
              </div>
            ))}
          </div>
        </div>
      )}

      {assignmentsToApply.length === 0 && (
        <div className={styles.warningBox} data-testid="review-and-confirm-warning-box">
          <Warning20Regular style={{ color: tokens.colorPaletteYellowForeground2, marginTop: '2px' }} data-testid="review-and-confirm-warning-icon" />
          <Text className={styles.warningText} data-testid="review-and-confirm-warning-text">
            No domains selected for assignment. All domains will be skipped.
          </Text>
        </div>
      )}

      <div className={styles.actions} data-testid="review-and-confirm-actions">
        <Button
          appearance="subtle"
          onClick={onBack}
          icon={<ArrowLeft20Regular data-testid="review-and-confirm-back-icon" />}
          disabled={applying}
          data-testid="review-and-confirm-back"
        >
          Back
        </Button>

        <Button
          appearance="primary"
          onClick={handleApply}
          icon={<Checkmark20Regular data-testid="review-and-confirm-apply-icon" />}
          iconPosition="after"
          disabled={applying || assignmentsToApply.length === 0}
          data-testid="review-and-confirm-apply"
        >
          Apply Assignments ({assignmentsToApply.length})
        </Button>
      </div>
    </div>
  );
};
